import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { Form } from "semantic-ui-react";
import {
  FurtherInstructionsGpt3,
  PromptConfigOverwrite,
  GptModel,
  GptProviders,
  PARAMETER_CONFIGS,
  StringParameter,
  ParameterType,
  NumberParameter,
} from "./types";
import { Text } from "../../components/Text";
import debounce from "lodash/debounce";
import { Button, Icon } from "../../components/tailwind";
import {
  PromptBooleanField,
  PromptSliderField,
  PromptSelectField,
} from "./PromptFormInputs/InputFields";

export type Props = {
  selectedModelName: string;
  selectedModel: GptModel;
  modelsConfig?: { [key: string]: any };
  advancedParameters: {
    further_instructions: string[] | FurtherInstructionsGpt3[];
    config_overwrite: PromptConfigOverwrite;
  };
  collectAdvancedParameters: (
    parameters: Partial<{
      further_instructions: string[] | FurtherInstructionsGpt3[];
      config_overwrite: PromptConfigOverwrite;
    }>
  ) => void;
  showUseWebSearch: boolean;
  useWebSearch: boolean;
  setUseWebSearch: (value: boolean) => void;
};

export const PromptFormModelSettings: React.FC<Props> = ({
  selectedModelName,
  selectedModel,
  modelsConfig,
  advancedParameters,
  collectAdvancedParameters,
  showUseWebSearch,
  useWebSearch,
  setUseWebSearch,
}) => {
  const currentConfig = useMemo(() => {
    const overwriteConfig =
      advancedParameters.config_overwrite?.[selectedModelName];
    return overwriteConfig || modelsConfig;
  }, [advancedParameters.config_overwrite, selectedModelName, modelsConfig]);

  const [textareaValue, setTextareaValue] = useState<string>("");
  const isEditingRef = useRef(false);
  const lastSyncedModelRef = useRef<string | undefined>(undefined);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      selectedModelName !== lastSyncedModelRef.current ||
      !isEditingRef.current
    ) {
      const configString = JSON.stringify(currentConfig, null, 4);
      setTextareaValue(configString);
      lastSyncedModelRef.current = selectedModelName;
      isEditingRef.current = false;
    }
  }, [currentConfig, selectedModelName]);

  const supportedParameters = selectedModel?.supported_parameters || [];

  const getReasoningValue = (reasoningValue: any): string | undefined => {
    if (!reasoningValue) return undefined;
    if (typeof reasoningValue === "object" && reasoningValue.effort) {
      return reasoningValue.effort;
    }
    return undefined;
  };

  const updateParameterValue = useCallback(
    (paramName: string, value: number | string) => {
      if (!isMountedRef.current) {
        return;
      }
      const baseConfig =
        advancedParameters.config_overwrite?.[selectedModelName] || {};
      const copy = { ...advancedParameters.config_overwrite };

      // If value is "default", remove the parameter from config
      if (value === "default") {
        const updatedConfig = { ...baseConfig };
        delete updatedConfig[paramName];
        // If config is now empty, remove the entire model entry
        if (Object.keys(updatedConfig).length === 0) {
          delete copy[selectedModelName];
        } else {
          copy[selectedModelName] = updatedConfig;
        }
      } else {
        let transformedValue: number | string | { effort: string } = value;
        if (paramName === "reasoning" && typeof value === "string") {
          transformedValue = { effort: value };
        }

        const updatedConfig = {
          ...baseConfig,
          [paramName]: transformedValue,
        };
        copy[selectedModelName] = updatedConfig;
      }
      collectAdvancedParameters({ config_overwrite: copy });
    },
    [
      advancedParameters.config_overwrite,
      selectedModelName,
      collectAdvancedParameters,
    ]
  );

  const resetToDefaults = useCallback(() => {
    const copy = { ...advancedParameters.config_overwrite };
    copy[selectedModelName] = {};
    collectAdvancedParameters({ config_overwrite: copy });
    setUseWebSearch(false);
  }, [selectedModelName, collectAdvancedParameters, setUseWebSearch]);

  const [JSONParseError, setJSONParseError] = useState<string | null>(null);
  const renderBody = (): React.ReactNode => {
    // Separate slider configs from select configs
    const sliderConfigs = supportedParameters
      .filter((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        return (
          config &&
          ((config as NumberParameter).type === ParameterType.FLOAT ||
            (config as NumberParameter).type === ParameterType.INT)
        );
      })
      .map((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        // Type guard: we know config exists and has min/max/step from the filter
        if (
          !config ||
          (config as NumberParameter).type === ParameterType.STRING
        ) {
          return null;
        }
        // Provide default value to prevent controlled/uncontrolled input warning
        const paramValue = currentConfig?.[paramName];
        const defaultValue =
          paramValue !== undefined
            ? paramValue
            : (config as NumberParameter).min ?? 0;
        return {
          name: paramName,
          config,
          value: defaultValue,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    const selectConfigs = supportedParameters
      .filter((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        return (
          config &&
          config.type === ParameterType.STRING &&
          (config as StringParameter).options
        );
      })
      .map((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        // Type guard: we know config exists and has options from the filter
        if (
          !config ||
          (config as StringParameter).type !== ParameterType.STRING
        ) {
          return null;
        }

        // Extract reasoning value from {"effort": "low"} format
        const overwriteConfig =
          advancedParameters.config_overwrite?.[selectedModelName];
        const hasOverwrite = !!overwriteConfig?.[paramName];

        // If no overwrite, use "default" as the value
        let value: string;
        if (!hasOverwrite) {
          value = "default";
        } else if (paramName === "reasoning") {
          const extractedValue = getReasoningValue(currentConfig?.[paramName]);
          value = extractedValue || (config as StringParameter).options[0];
        } else {
          value =
            currentConfig?.[paramName] ||
            (config as StringParameter).options[0];
        }

        return {
          name: paramName,
          config,
          value,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    return (
      <span data-testid="prompt-form-model-settings-inputs">
        {GptProviders.OPENROUTER === selectedModel?.provider ? (
          <Form.Field>
            {selectConfigs.map(({ name, config, value }) => (
              <PromptSelectField
                key={name}
                testId={`copy-assistant-template-form-${name}`}
                caption={config.caption || ""}
                label={config.label}
                placeholder={`Select ${config.label}`}
                dropdownOptions={[
                  {
                    key: "default",
                    value: "default",
                    text: "Default",
                  },
                  ...(config as StringParameter).options.map((option) => ({
                    key: option,
                    value: option,
                    text: option.charAt(0).toUpperCase() + option.slice(1),
                  })),
                ]}
                value={value}
                setValue={(newValue: string) =>
                  updateParameterValue(name, newValue)
                }
              />
            ))}
            {showUseWebSearch && (
              <>
                <PromptBooleanField
                  testId="copy-assistant-template-form-use-web-search"
                  caption="Give the AI access to browse the web."
                  label={"Web Search"}
                  note="Please note that websearch cost additional credits."
                  value={useWebSearch}
                  setValue={setUseWebSearch}
                />
              </>
            )}
            {sliderConfigs.map(({ name, config, value }) => (
              <PromptSliderField
                key={name}
                testId={`copy-assistant-template-form-${name}`}
                caption={(config as NumberParameter).caption}
                label={(config as NumberParameter).label}
                min={(config as NumberParameter).min}
                max={(config as NumberParameter).max}
                step={(config as NumberParameter).step}
                value={value}
                setValue={(newValue: number) =>
                  updateParameterValue(name, newValue)
                }
              />
            ))}{" "}
            <div className="tw-mb-4">
              <Button
                type="button"
                data-testid="prompt-form-model-settings-reset-button"
                variant="secondary-alt"
                size="sm"
                icon
                onClick={resetToDefaults}
              >
                Reset to OpenRouter default
              </Button>
            </div>
          </Form.Field>
        ) : (
          <Form.Field>
            <p className="tw-mx-0 tw-flex tw-items-center tw-gap-1">
              <Icon
                name="warning"
                className="tw-text-lg tw-leading-[1] tw-text-yellow-500"
              />{" "}
              If this is not used correctly, it can break the prompt.
            </p>
            <label>Provider API Config</label>
            <Text color="grey" className="descriptive-helper-text">
              In a JSON format, you can override the default parameters for the
              provider API. Please refer to the provider API documentation for
              the available parameters.
            </Text>
            <textarea
              data-testid="prompt-form-model-settings-config-overwrite-input"
              value={textareaValue}
              onChange={({ target }): void => {
                isEditingRef.current = true;
                setTextareaValue(target.value);
                updateConfigOverwrite(target.value);
              }}
            />
          </Form.Field>
        )}
        {JSONParseError && (
          <Text
            color="red"
            className="descriptive-helper-text"
            testId="prompt-form-model-settings-config-overwrite-error"
          >
            {JSONParseError}
          </Text>
        )}
      </span>
    );
  };

  const updateConfigOverwrite = useCallback(
    debounce((value: string): void => {
      if (!isMountedRef.current) {
        return;
      }
      setJSONParseError(null);
      if (!value) {
        const copy = { ...advancedParameters.config_overwrite };
        if (copy[selectedModelName]) {
          delete copy[selectedModelName];
        }
        collectAdvancedParameters({ config_overwrite: copy });
        isEditingRef.current = false;
        return;
      }
      try {
        const parsedValue = JSON.parse(value);
        const copy = { ...advancedParameters.config_overwrite };
        copy[selectedModelName] = parsedValue;

        collectAdvancedParameters({ config_overwrite: copy });
        isEditingRef.current = false;
      } catch (e) {
        if (isMountedRef.current) {
          setJSONParseError(e.message);
        }
      }
    }, 1000),
    [
      advancedParameters.config_overwrite,
      selectedModelName,
      collectAdvancedParameters,
    ]
  );

  useEffect(() => {
    return () => {
      updateConfigOverwrite.cancel();
    };
  }, [updateConfigOverwrite]);

  return <>{renderBody()}</>;
};
